// Refactored chat API using centralized apiClient
import { apiClient, extractPagination } from './client';
import { Message, Conversation } from '../models/Conversation';

/** Response when a conversation cannot be created due to an error */
export interface ConversationErrorResponse {
  error: string;
}

/** Error response for sending a message */
export interface SendMessageErrorResponse {
  success: false;
  error: string;
  message?: string;
  redirect?: string;
}

/** Response for sending a message - can be the Message directly on success, or an error object */
export type SendMessageResponse = Message | SendMessageErrorResponse;

/** Error response for sending a flirt */
export interface SendFlirtErrorResponse {
  success: false;
  error: string;
  message?: string;
}

/** Response for sending a flirt - can be the Message directly on success, or an error object */
export type SendFlirtResponse = Message | SendFlirtErrorResponse;

/** Success response for sending a gift */
export interface SendGiftSuccessResponse {
  success: true;
  message: Message;
}

/** Error response for sending a gift */
export interface SendGiftErrorResponse {
  success: false;
  error: string;
  message?: string;
  redirect?: string;
}

/** Response for sending a gift */
export type SendGiftResponse = SendGiftSuccessResponse | SendGiftErrorResponse;

/** Response for archiving a conversation */
export interface ArchiveConversationResponse {
  success: boolean;
  archived?: boolean;
  error?: string;
}

export async function fetchInboxConversations(page = 1, archived = false): Promise<{ data: Conversation[]; pageCount: number; totalCount: number }>{
  const result = await apiClient.get<Conversation[]>('/chat/api/inbox', { page, archived: archived ? 'true' : undefined });
  if (!result.ok) {
    console.error('Error fetching inbox conversations:', result.error);
    return { data: [], pageCount: 0, totalCount: 0 };
  }
  const { pageCount, totalCount } = extractPagination(result.response);
  return { data: result.data || [], pageCount: pageCount ?? 0, totalCount: totalCount ?? 0 };
}

export async function fetchConversationByUserId(userId: number): Promise<Conversation | ConversationErrorResponse | null> {
  const result = await apiClient.get<Conversation | ConversationErrorResponse>('/chat/api/conversation', { userId });
  if (!result.ok) {
    console.error('Error fetching conversation by user ID:', result.error);
    return null;
  }
  // Check for error response
  if (result.data && 'error' in result.data) {
    return { error: result.data.error };
  }
  return result.data ?? null;
}

export async function fetchConversationById(conversationId: number): Promise<Conversation | null> {
  const result = await apiClient.get<Conversation>('/chat/api/conversation', { conversationId });
  if (!result.ok) {
    console.error('Error fetching conversation by ID:', result.error);
    return null;
  }
  return result.data ?? null;
}

export async function fetchMessagesByConversationId(conversationId: number, page: number): Promise<{ data: Message[]; pageCount: number }>{
  const result = await apiClient.get<Message[]>('/chat/api/messages', { conversationId, page });
  if (!result.ok) {
    console.error('Error fetching messages by conversation ID:', result.error);
    return { data: [], pageCount: 0 };
  }
  const { pageCount } = extractPagination(result.response);
  return { data: result.data || [], pageCount };
}

export async function sendMessage(recipientId: number, formData: FormData): Promise<SendMessageResponse> {
  const result = await apiClient.postForm<SendMessageResponse>(`/chat/send/message?json=1&id=${recipientId}`, formData);
  if (!result.ok) {
    console.error('Error sending message:', result.error);
    throw result.error;
  }
  return result.data!;
}

export async function sendFlirt(recipientId: number): Promise<SendFlirtResponse> {
  const result = await apiClient.postJson<SendFlirtResponse>(`/chat/send/flirt?json=1&id=${recipientId}`);
  if (!result.ok) {
    console.error('Error sending flirt:', result.error);
    throw result.error;
  }
  return result.data!;
}

export async function sendGift(recipientId: number, giftId: number): Promise<SendGiftResponse> {
  const result = await apiClient.postJson<SendGiftResponse>(`/chat/send/gift?json=1&id=${recipientId}&giftId=${giftId}`);
  if (!result.ok) {
    console.error('Error sending gift:', result.error);
    throw result.error;
  }
  return result.data!;
}

export async function archiveConversation(conversationId: number): Promise<ArchiveConversationResponse> {
  const fd = new FormData();
  fd.append('conversationId', String(conversationId));
  const result = await apiClient.postForm<ArchiveConversationResponse>('/chat/api/archive', fd);
  if (!result.ok) {
    console.error('Error archiving conversation:', result.error);
    throw result.error;
  }
  return result.data!;
}
